import { ElementRef, useCallback, useContext, useEffect, useRef } from 'react'

import { useTheme } from '@emotion/react'
import { useTranslation } from 'react-i18next'

import { Country } from '@cash-web/protos/squareup/common/countries.pb'
import { Money } from '@cash-web/protos/squareup/common/currency.pb'
import { InstrumentType } from '@cash-web/protos/squareup/common/instrument.pb'
import { CashInstrumentType } from '@cash-web/protos/squareup/franklin/cash_instrument_type.pb'
import logger from '@cash-web/shared/util-logger'
import { useMutationAuthorizeDigitalWalletPayment } from '@cash-web/shared-data-access-api/react-query/useMutationAuthorizeDigitalWalletPayment'
import { ErrorContext } from '@cash-web/shared-data-access-error'
import { useLogCashWebCdfEvent } from '@cash-web/shared-feature-analytics'
import { useFlowContext } from '@cash-web/shared-flow-data-access-context'
import { WebDebugLog, WebInteractClick, WebViewViewComponent } from '@squareup/customer-data-events-web'

import { ValidMoney } from '../validity'
import { fetchMerchantSession } from './fetchMerchantSession'
import { formatAmount, getSupportedNetworks, toGlobalAddress } from './utils'

const CDF_COMPONENT_NAME = 'Apple Pay Button'

export const userCanUseApplePay = () => {
  try {
    return Boolean(window.ApplePaySession && ApplePaySession.canMakePayments())
  } catch {
    // Return false, user can't use Apple pay.
    return false
  }
}

const logError = (error: unknown) => {
  if (error instanceof Error) {
    logger.error(error)
  } else {
    logger.error(new Error(`ApplePayButton: ${error}`))
  }
}

export const ApplePayButton = ({
  amount,
  processingFee,
  cashInstrumentTypes,
  requirePayerName,
  requirePhoneNumber,
  requirePayerEmail,
  requireBillingAddress,
  requireShippingAddress,
  supportedInstrumentTypes,
  allowableIssuerCountries,
  setSubmitting,
}: {
  amount: ValidMoney
  processingFee?: Money
  cashInstrumentTypes: InstrumentType[]
  requirePayerName?: boolean
  requirePhoneNumber?: boolean
  requirePayerEmail?: boolean
  requireBillingAddress?: boolean
  requireShippingAddress?: boolean
  supportedInstrumentTypes: CashInstrumentType[]
  allowableIssuerCountries: Country[]
  setSubmitting?: (submitting: boolean) => void
}) => {
  const { t } = useTranslation()
  const logCdfEvent = useLogCashWebCdfEvent()
  const { mutateAsync: authorizeDigitalWalletPayment } = useMutationAuthorizeDigitalWalletPayment()
  const buttonRef = useRef<ElementRef<'div'>>(null)
  const flow = useFlowContext()
  const { darkMode } = useTheme()
  const { clearError, setError } = useContext(ErrorContext)

  const validateMerchant = useCallback(async () => {
    try {
      return await fetchMerchantSession()
    } catch (err) {
      logCdfEvent(
        WebDebugLog({
          action: 'Apple Pay',
          description: 'Error fetching merchant session',
        })
      )
      logError(err)
      return undefined
    }
  }, [logCdfEvent])

  const onClick = useCallback(async () => {
    clearError()

    logCdfEvent(WebInteractClick({ componentName: CDF_COMPONENT_NAME }))

    // TODO: remove this if we eventually support shipping address
    if (requireShippingAddress) {
      throw new Error('Shipping address is not supported for Apple Pay in BlockerAuthorizeDigitalWalletPayment')
    }

    // https://developer.apple.com/documentation/applepayontheweb/applepaycontactfield/
    const billingContactFields: ApplePayJS.ApplePayPaymentRequest['requiredBillingContactFields'] = []
    if (requireBillingAddress) {
      billingContactFields.push('postalAddress')

      if (requirePayerName) billingContactFields.push('name')
      if (requirePhoneNumber) billingContactFields.push('phone')
      if (requirePayerEmail) billingContactFields.push('email')
    }

    const merchantCapabilities: ApplePayJS.ApplePayPaymentRequest['merchantCapabilities'] = ['supports3DS']
    if (supportedInstrumentTypes.includes(CashInstrumentType.CREDIT_CARD)) {
      merchantCapabilities.push('supportsCredit')
    }
    if (supportedInstrumentTypes.includes(CashInstrumentType.DEBIT_CARD)) {
      merchantCapabilities.push('supportsDebit')
    }

    const paymentRequest: ApplePayJS.ApplePayPaymentRequest = {
      countryCode: 'US',
      currencyCode: amount.currency_code,
      merchantCapabilities,
      supportedNetworks: getSupportedNetworks(cashInstrumentTypes),
      supportedCountries: allowableIssuerCountries,

      requiredBillingContactFields: billingContactFields,

      total: {
        label: 'Cash App',
        amount: formatAmount(amount.amount),
      },
    }

    const session = new ApplePaySession(3, paymentRequest)

    session.onvalidatemerchant = async event => {
      const merchantSession = await validateMerchant()
      if (!merchantSession) {
        setError(
          t('Sorry, there was an error. Try again?', {
            context: 'Error text. unknown error',
          })
        )
        logCdfEvent(
          WebDebugLog({
            action: 'Apple Pay',
            description: 'Apple Pay Merchant Session is undefined',
          })
        )
        logger.error(new Error('Apple Pay Merchant Session is undefined'))
      }

      session.completeMerchantValidation(merchantSession)
    }

    session.onpaymentmethodselected = ({ paymentMethod }) => {
      let newAmount = amount.amount
      let newLineItems

      if (paymentMethod.type === 'credit' && processingFee?.amount) {
        newAmount += processingFee.amount
        const formattedFeePercentage = Number(((processingFee.amount / amount.amount) * 100).toFixed())

        newLineItems = [
          {
            label: 'Payment amount',
            amount: formatAmount(amount.amount),
          },
          {
            label: `Credit card fee (${formattedFeePercentage}%)`,
            amount: formatAmount(processingFee.amount),
          },
        ]
      }

      session.completePaymentMethodSelection({
        newTotal: {
          label: 'Cash App',
          amount: formatAmount(newAmount),
        },
        newLineItems,
      })
    }

    session.onpaymentauthorized = async event => {
      setSubmitting?.(true)

      try {
        const cashAppAuthorizeResponse = authorizeDigitalWalletPayment({
          apple_pay_payment_token: event.payment.token.paymentData,
          // The backend is expecting the full name in the first_name field.
          first_name: [event.payment.billingContact?.givenName, event.payment.billingContact?.familyName]
            .filter(Boolean)
            .join(' '),
          phone_number: event.payment.billingContact?.phoneNumber,
          email: event.payment.billingContact?.emailAddress,
          address: toGlobalAddress(event.payment.billingContact),
        })

        session.completePayment({
          status: ApplePaySession.STATUS_SUCCESS,
        })

        await flow.updateWithResponse({
          response: cashAppAuthorizeResponse,
          queriesToInvalidate: [],
        })
      } catch (err) {
        setError(
          t('Sorry, there was an error. Try again?', {
            context: 'Error text. unknown error',
          })
        )

        logError(err)
        logCdfEvent(
          WebDebugLog({
            action: 'Apple Pay',
            description: 'Error authorizing digital wallet payment',
          })
        )
      }
    }

    try {
      session.begin()
    } catch (err) {
      setError(
        t('Sorry, there was an error. Try again?', {
          context: 'Error text. unknown error',
        })
      )
      logCdfEvent(
        WebDebugLog({
          action: 'Apple Pay',
          description: 'Apple Pay Session failed to begin',
        })
      )
      logError(err)
    }
  }, [
    clearError,
    logCdfEvent,
    requireShippingAddress,
    requireBillingAddress,
    supportedInstrumentTypes,
    amount.currency_code,
    amount.amount,
    cashInstrumentTypes,
    allowableIssuerCountries,
    requirePayerName,
    requirePhoneNumber,
    requirePayerEmail,
    validateMerchant,
    setError,
    t,
    processingFee?.amount,
    setSubmitting,
    authorizeDigitalWalletPayment,
    flow,
  ])

  useEffect(() => {
    const button = buttonRef.current

    if (button !== null) {
      button.addEventListener('click', onClick)
    }

    return () => {
      if (button !== null) {
        button.removeEventListener('click', onClick)
      }
    }
  }, [onClick])

  useEffect(() => {
    logCdfEvent(WebViewViewComponent({ componentName: CDF_COMPONENT_NAME }))
  }, [logCdfEvent])

  return (
    <>
      {/* @ts-expect-error - apple-pay-button is a custom tag provided by the Apple Pay SDK */}
      <apple-pay-button
        buttonstyle={darkMode ? 'white' : 'black'}
        type="plain"
        locale="en-US"
        ref={buttonRef}
        data-testid="apple-pay-button"
      />
    </>
  )
}
